import { useCallback, useRef, useState } from 'react';
import type { AssetShareAccess } from 'fym-shared/src/music24/assetShares';
import { useTRPCClientAPI } from '../../../trpc/trpc';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useApplyFreshUserToken } from '../../team/useApplyFreshUserToken';
import { sharedAssetRouteParams } from './sharedAssetRoute';

/** `opening`: the switch to the workspace of the asset runs. `failed`: the switch did not work. */
export type OpenSharedAssetState = 'idle' | 'opening' | 'failed';

export interface OpenSharedAsset {
  state: OpenSharedAssetState;
  /** Opens the shared asset in its release (replace). Does nothing without access or while a switch runs. */
  open: () => void;
}

/**
 * Opens a shared asset for a member of its workspace (owner fix 2026-10-19: a signed-in member
 * never sees the public share page; the page sends the member on by itself). It goes to the
 * release page with the asset viewer open on the shared revision (the player with the controls on
 * the right). When the session acts in another workspace, it switches first (`workspaces.switch`,
 * then the token swap that resets the caches, as the workspace switcher does). The move REPLACES
 * the share page in the history, so Back does not return to a page that sends the member on again.
 * A failed switch keeps the share page with "Open in release" as a retry.
 */
export function useOpenSharedAsset(access: AssetShareAccess | null | undefined): OpenSharedAsset {
  const router = useSMLRouter();
  const trpcClient = useTRPCClientAPI();
  const applyToken = useApplyFreshUserToken();
  const [state, setState] = useState<OpenSharedAssetState>('idle');
  const running = useRef(false);

  const open = useCallback(() => {
    if (access === null || access === undefined || running.current) {
      return;
    }
    const params = sharedAssetRouteParams(access);
    if (access.isActiveWorkspace) {
      router.replace(SMLRoutes.promoteRelease, params);
      return;
    }
    running.current = true;
    setState('opening');
    trpcClient.workspaces.switch
      .mutate({ licenseId: access.licenseId })
      .then(({ token }) => applyToken(token))
      .then(() => {
        running.current = false;
        setState('idle');
        router.replace(SMLRoutes.promoteRelease, params);
      })
      .catch((error: unknown) => {
        console.error(error);
        running.current = false;
        setState('failed');
      });
  }, [access, applyToken, router, trpcClient]);

  return { state, open };
}
